import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';

import {
  IOT_VISIBILITY_UNKNOWN_KEY,
  iotStationVisibilityKey,
  type IoTStationLayerKey,
} from '../types/map.types';

/** RGBA tuple for Deck.gl `getFillColor` / `getLineColor` and Cesium `Color.fromBytes`. */
export type RgbaTuple = [number, number, number, number];

type Rgb = [number, number, number];

const TYPE_RGB: Record<IoTStationLayerKey, Rgb> = {
  water_level: [14, 165, 233],
  rainfall: [56, 189, 248],
  soil_moisture: [180, 83, 9],
  temperature: [249, 115, 22],
  humidity: [13, 148, 136],
  geophysical: [139, 92, 246],
  other: [100, 116, 139],
};

const TYPE_RGB_LOOKUP = TYPE_RGB as Record<string, Rgb>;

function hashStringToRgb(input: string): Rgb {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 31 + input.charCodeAt(i)) | 0;
  }
  const r = 72 + (Math.abs(h) % 160);
  const g = 72 + (Math.abs(h >> 7) % 160);
  const b = 72 + (Math.abs(h >> 14) % 160);
  return [r, g, b];
}

const GEOPHYSICAL_SUB_RGB: Record<NonNullable<IoTDataPoint['geophysicalSubtype']>, Rgb> = {
  seismic: [244, 63, 94],
  atmospheric: [34, 211, 238],
  ionospheric: [129, 140, 248],
  deformation: [245, 158, 11],
};

const GEOPHYSICAL_SUB_RGB_LOOKUP = GEOPHYSICAL_SUB_RGB as Record<string, Rgb>;

function clamp255(n: number): number {
  return Math.min(255, Math.max(0, Math.round(n)));
}

function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const u = Math.min(1, Math.max(0, t));
  return [
    clamp255(a[0] + (b[0] - a[0]) * u),
    clamp255(a[1] + (b[1] - a[1]) * u),
    clamp255(a[2] + (b[2] - a[2]) * u),
  ];
}

function toRgba(rgb: Rgb, a: number): RgbaTuple {
  return [rgb[0], rgb[1], rgb[2], clamp255(a)];
}

/**
 * Base RGB by station kind (`type` / `other`) and by `geophysicalSubtype` when geophysical.
 */
export function getIotStationTypeRgb(station: IoTDataPoint): Rgb {
  const catalog = station.sensorTypeCatalogRgb;
  if (catalog && catalog.length === 3) {
    return [catalog[0], catalog[1], catalog[2]];
  }
  if (station.type === 'geophysical' && station.geophysicalSubtype) {
    return GEOPHYSICAL_SUB_RGB[station.geophysicalSubtype];
  }
  const vk = iotStationVisibilityKey(station);
  const preset = TYPE_RGB_LOOKUP[vk];
  if (preset) return preset;
  return hashStringToRgb(vk);
}

function getIotLayerKeyDotRgb(visibilityKey: string): Rgb {
  const k = visibilityKey.trim().toLowerCase();
  if (!k || k === IOT_VISIBILITY_UNKNOWN_KEY) {
    return TYPE_RGB.other;
  }
  const preset = TYPE_RGB_LOOKUP[k];
  if (preset) return preset;
  const geo = GEOPHYSICAL_SUB_RGB_LOOKUP[k];
  if (geo) return geo;
  return hashStringToRgb(k);
}

/**
 * Màu CSS `rgb(...)` cho chấm loại trạm IoT trong Layer Panel — cùng palette với marker trên bản đồ.
 * Dùng khi API không trả `color` hoặc mã không khớp preset.
 */
export function getIotLayerKeyDotCssColor(visibilityKey: string): string {
  const rgb = getIotLayerKeyDotRgb(visibilityKey);
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

export interface IotStationMarkerStyle {
  haloFill: RgbaTuple;
  coreFill: RgbaTuple;
  ringLine: RgbaTuple;
  haloLine: RgbaTuple;
  /** Deck.gl `getRadius` (pixels) for outer halo. */
  haloRadiusPx: number;
  /** Deck.gl core radius (pixels). */
  coreRadiusPx: number;
  /** Deck.gl `lineWidthMinPixels` for core stroke. */
  coreRingWidthPx: number;
  /** Cesium `PointGraphics.pixelSize` for core disc. */
  cesiumCorePixelSize: number;
  cesiumHaloPixelSize: number;
  cesiumOutlineWidth: number;
}

/** Multi-layer “light bulb” stack for Deck.gl (outer → mid → body → hotspot). */
export interface IotStationBulbLayerSpec {
  radiusPx: number;
  fill: RgbaTuple;
  line: RgbaTuple;
  lineWidthPx: number;
  stroked: boolean;
}

export interface IotStationBulbStyle {
  outer: IotStationBulbLayerSpec;
  mid: IotStationBulbLayerSpec;
  body: IotStationBulbLayerSpec;
  hotspot: Pick<IotStationBulbLayerSpec, 'radiusPx' | 'fill'>;
  /** Cesium point diameters (approx.2× Deck radius + padding). */
  cesiumGlowPx: number;
  cesiumBodyPx: number;
  cesiumHotspotPx: number;
}

const WARN_MIX = 0.38;
const DANGER_MIX = 0.62;

export function isIotStationBreached(station: IoTDataPoint): boolean {
  return Boolean(station.thresholdBreached);
}

export function isIotStationDangerAlert(station: IoTDataPoint): boolean {
  return (
    Boolean(station.thresholdBreached) &&
    (station.thresholdSeverity === 'danger' || !station.thresholdSeverity)
  );
}

const SLATE_900: Rgb = [15, 23, 42];
const WARN_DARK: Rgb = [120, 53, 15];
const DANGER_DARK: Rgb = [127, 29, 29];

function rgbaAlphaScale(c: RgbaTuple, mul: number): RgbaTuple {
  const a = clamp255(c[3] * mul);
  return [c[0], c[1], c[2], a];
}

/**
 * Phase in radians (e.g. from accumulating time). Drives gentle “breathing”; stronger when breached.
 */
export function iotStationPulseFactors(
  phase: number,
  station: IoTDataPoint,
): { outerRadiusMul: number; glowAlphaMul: number; hotspotRadiusMul: number } {
  const breached = Boolean(station.thresholdBreached);
  const isDanger = isIotStationDangerAlert(station);
  const oAmp = isDanger ? 0.34 : breached ? 0.22 : 0.095;
  const outerRadiusMul = 1 + oAmp * Math.sin(phase);
  const glowAlphaMul =
    0.78 + (isDanger ? 0.34 : breached ? 0.24 : 0.18) * Math.sin(phase * 1.12 + 0.35);
  const hotspotRadiusMul =
    1 + (isDanger ? 0.18 : breached ? 0.14 : 0.06) * Math.sin(phase * 1.65 + 0.2);
  return { outerRadiusMul, glowAlphaMul, hotspotRadiusMul };
}

/**
 * “Light bulb” radiance: soft outer bloom, glass mid, tinted body, bright hotspot.
 */
export function getIotStationBulbStyle(station: IoTDataPoint): IotStationBulbStyle {
  const base = getIotStationTypeRgb(station);
  const breached = Boolean(station.thresholdBreached);
  const isWarn = breached && station.thresholdSeverity === 'warning';
  const isDanger =
    breached && (station.thresholdSeverity === 'danger' || !station.thresholdSeverity);

  let bodyRgb = base;
  if (isWarn) bodyRgb = mixRgb(base, [250, 204, 21], WARN_MIX);
  else if (isDanger) bodyRgb = mixRgb(base, [248, 113, 113], DANGER_MIX);

  const outerRgb: Rgb = !breached
    ? base
    : isWarn
      ? mixRgb(base, [250, 204, 21], 0.45)
      : mixRgb(base, [239, 68, 68], isDanger ? 0.58 : 0.42);

  const glassRgb = mixRgb(bodyRgb, [255, 255, 255], 0.42);
  const midRgb = mixRgb(bodyRgb, [255, 255, 255], 0.28);

  const darkEdgeRgb: Rgb = !breached
    ? mixRgb(bodyRgb, SLATE_900, 0.6)
    : isWarn
      ? mixRgb(bodyRgb, WARN_DARK, 0.55)
      : mixRgb(bodyRgb, DANGER_DARK, 0.6);
  const ringBody: RgbaTuple = toRgba(darkEdgeRgb, 255);

  const ringMid: RgbaTuple = toRgba(darkEdgeRgb, breached ? 170 : 130);
  const ringOuter: RgbaTuple = toRgba(darkEdgeRgb, breached ? 110 : 85);

  const outer: IotStationBulbLayerSpec = {
    radiusPx: isDanger ? 30 : breached ? 25 : 22,
    fill: toRgba(outerRgb, isDanger ? 130 : breached ? 95 : 60),
    line: ringOuter,
    lineWidthPx: isDanger ? 1.6 : 1.1,
    stroked: true,
  };
  const mid: IotStationBulbLayerSpec = {
    radiusPx: isDanger ? 21 : breached ? 18 : 16,
    fill: toRgba(midRgb, isDanger ? 195 : breached ? 170 : 135),
    line: ringMid,
    lineWidthPx: isDanger ? 1.55 : 1.25,
    stroked: true,
  };
  const body: IotStationBulbLayerSpec = {
    radiusPx: isDanger ? 13 : breached ? 11.5 : 10.5,
    fill: toRgba(glassRgb, 255),
    line: ringBody,
    lineWidthPx: !breached ? 2.5 : isDanger ? 3.75 : 2.85,
    stroked: true,
  };
  const hotspot: IotStationBulbStyle['hotspot'] = {
    radiusPx: 3.5,
    fill: [255, 254, 248, 252],
  };

  return {
    outer,
    mid,
    body,
    hotspot,
    cesiumGlowPx: Math.round(outer.radiusPx * 2) + 12,
    cesiumBodyPx: Math.round(body.radiusPx * 2) + 8,
    cesiumHotspotPx: Math.round(hotspot.radiusPx * 2) + 4,
  };
}

/**
 * Unified 2D/3D marker: type-colored core, soft halo, alert ring (warning / danger).
 */
export function getIotStationMarkerStyle(station: IoTDataPoint): IotStationMarkerStyle {
  const base = getIotStationTypeRgb(station);
  const breached = Boolean(station.thresholdBreached);
  const isWarn = breached && station.thresholdSeverity === 'warning';
  const isDanger =
    breached && (station.thresholdSeverity === 'danger' || !station.thresholdSeverity);

  let coreRgb = base;
  if (isWarn) coreRgb = mixRgb(base, [250, 204, 21], WARN_MIX);
  else if (isDanger) coreRgb = mixRgb(base, [248, 113, 113], DANGER_MIX);

  const ringLine: RgbaTuple = !breached
    ? [255, 255, 255, 238]
    : isWarn
      ? [254, 240, 138, 255]
      : [254, 202, 202, 255];

  const haloLine: RgbaTuple = !breached
    ? [255, 255, 255, 72]
    : isWarn
      ? [253, 224, 71, 180]
      : [252, 165, 165, 190];

  const haloFill: RgbaTuple = !breached
    ? [base[0], base[1], base[2], 48]
    : isWarn
      ? [250, 204, 21, 72]
      : [248, 113, 113, 78];

  const coreRadiusPx = !breached ? 7.5 : 8;
  const haloRadiusPx = !breached ? 16 : 18;
  const coreRingWidthPx = !breached ? 2 : isDanger ? 3.25 : 2.75;

  return {
    haloFill,
    coreFill: toRgba(coreRgb, !breached ? 250 : 255),
    ringLine,
    haloLine,
    haloRadiusPx,
    coreRadiusPx,
    coreRingWidthPx,
    cesiumCorePixelSize: Math.round(coreRadiusPx * 2) + 2,
    cesiumHaloPixelSize: Math.round(haloRadiusPx * 2) + 4,
    cesiumOutlineWidth: !breached ? 2 : isDanger ? 3 : 2,
  };
}

/** Core fill RGBA (legacy helper; prefer {@link getIotStationMarkerStyle}). */
export function getIotStationFillRgba(station: IoTDataPoint): RgbaTuple {
  return getIotStationMarkerStyle(station).coreFill;
}

/** Pulse modulation on fill alpha (outer / mid “bloom”). */
export function applyIotBulbPulseAlpha(fill: RgbaTuple, glowAlphaMul: number): RgbaTuple {
  return rgbaAlphaScale(fill, glowAlphaMul);
}
